import { useState } from "react";

import {
  useGetSearchFilterSetsQuery,
  type Group,
  type User,
  useDeleteSearchFilterSetMutation,
} from "~/client/generated/schema";
import { emptyConnectionWithTotalCount, type ConnectionWithTotalCount } from "~/client/utils/relay";

export type SearchFilterSetItem = Readonly<{
  id: Readonly<string>;
  excludeKeywords: ReadonlyArray<string>;
  authorUsers: ReadonlyArray<Partial<User>>;
  groups: ReadonlyArray<Partial<Group>>;
  folders: ReadonlyArray<{
    id: Readonly<string>;
    fullName: Readonly<string>;
    group: Readonly<Partial<Group>>;
  }>;
  dateGte?: Readonly<string | null>;
  isLikedByMe: Readonly<boolean>;
  resourceTypes: ReadonlyArray<string>;
  isArchived: Readonly<boolean>;
}>;

const coerceConnection = (sets) =>
  sets ? (sets as ConnectionWithTotalCount<SearchFilterSetItem>) : emptyConnectionWithTotalCount<SearchFilterSetItem>();

const getCurrentItems = (
  all: ConnectionWithTotalCount<SearchFilterSetItem>,
  deletedIds: Array<string>,
): ConnectionWithTotalCount<SearchFilterSetItem> => {
  const edges = all.edges.filter((edge) => !deletedIds.includes(edge.node.id));
  return { ...all, edges };
};

export const useSavedSearchFilterSets = (): {
  items: ConnectionWithTotalCount<SearchFilterSetItem>;
  isLoading: boolean;
  handleDelete: (id: string) => void;
} => {
  const [deletedIds, setDeletedIds] = useState<Array<string>>([]);

  const { data, loading } = useGetSearchFilterSetsQuery({ variables: { first: 6 }, fetchPolicy: "network-only" });

  const [deleteSearchFilterSet] = useDeleteSearchFilterSetMutation();
  const handleDelete = (id: string) => {
    deleteSearchFilterSet({ variables: { input: { id } } });
    setDeletedIds([...deletedIds, id]);
  };

  return {
    items: getCurrentItems(coerceConnection(data?.searchFilterSets), deletedIds),
    isLoading: loading,
    handleDelete,
  };
};
